// =========================================================================
// Getting a signing link from here to the device that holds the Ledger.
//
// THE PROBLEM THIS SOLVES. The obvious handoff — an <a target="_blank"> — does
// not work from Telegram. The link opens in Telegram's own in-app browser,
// where `window.tronWeb` is undefined, so the co-sign page shows "TronLink was
// not detected" after three seconds. The operator's remaining moves are all
// worse: long-press → Copy link puts a live credential on the clipboard, and
// sharing it into a chat puts it somewhere permanent and synced. The co-sign
// design has an explicit rule that a signing URL must never enter a Telegram
// message; the only handoff that worked was the one that broke it.
//
// So: SCREEN TO CAMERA. The URL is rendered as a QR code and scanned by the
// phone holding TronLink and the Ledger. It never touches the clipboard, the
// share sheet, or a message.
//
// WHAT WAS DELIBERATELY NOT DONE: a `tronlinkoutside://` custom scheme. Custom
// schemes are not exclusive on Android — any app can declare the same intent
// filter and receive the credential. A verified App Link would be safe, but it
// requires TronLink to publish assetlinks.json for a domain we control, which
// is not available to us.
//
// The QR library lives in THIS bundle, never in the co-sign page: that page's
// value comes from being small enough to audit by eye and pin by hash.
// =========================================================================

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { makeMintGate } from '../lib/custody-screen';
import { X, Copy, ExternalLink, RefreshCw } from 'lucide-react';
import { StateLine, Pending } from './kit';
import { countdown } from '../lib/custody-format';
import { qrPath, QR_QUIET_ZONE } from '../lib/qr';
import { linkRefusal, refusalIsGoodNews } from '../lib/cosign-errors';
import type { SigningUrl } from '../lib/cosign-link';

export interface MintedLink {
  /**
   * Not the server's string: a link rebuilt on the signing site fixed in this
   * build (lib/cosign-link.ts). The type is what keeps the server's own link
   * from being drawn as the QR code.
   */
  url: SigningUrl;
  expiresAt: string;
}

/**
 * Render a QR as inline SVG. Exported for the deposit-address list (17 Oct
 * 2026): an address shown to a customer is scanned the same way a link is.
 *
 * SVG rather than canvas or a data URI: it stays crisp at whatever size the
 * screen gives it, which matters because a QR that has to be scanned from a
 * laptop screen by a phone held at arm's length is only as good as its
 * sharpness. Error-correction level M tolerates a little glare without
 * inflating the module count.
 */
export function QrSvg({ text, className, label = 'Signing link QR code' }: { text: string; className?: string; label?: string }) {
  // One path for the whole code rather than an element per module: a 57x57
  // code is 3249 potential nodes.
  const { d, size } = useMemo(() => qrPath(text), [text]);
  const quiet = QR_QUIET_ZONE;
  const total = size + quiet * 2;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${total} ${total}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={label}
    >
      <rect width={total} height={total} fill="#fff" />
      <g transform={`translate(${quiet},${quiet})`} fill="#000">
        <path d={d} />
      </g>
    </svg>
  );
}

export function CosignHandoff({
  open,
  onClose,
  title,
  subtitle,
  mint,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  /** Called when the sheet opens, so the credential's clock starts at intent. */
  mint: () => Promise<MintedLink>;
}) {
  const [link, setLink] = useState<MintedLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  // Only the LATEST opening's answer is shown (lib/custody-screen.ts): open A,
  // close, open B — A's link resolving last must never show as B's (Screens'
  // Astra review, 22 Oct 2026). A close invalidates everything in flight.
  const gate = useRef(makeMintGate());

  const getLink = useCallback(async () => {
    setBusy(true);
    setError(null);
    setLink(null);
    try {
      const answer = await gate.current.open(mint);
      if (answer.stale) return;
      setLink(answer.value);
    } catch (err) {
      setError(String((err as Error).message));
    }
    setBusy(false);
  }, [mint]);

  useEffect(() => {
    if (open) void getLink();
    // Nothing lingers once the sheet is closed: the credential is dropped from
    // state so a reopened sheet mints a fresh one rather than re-showing a
    // half-spent link — and an answer still in flight is dropped on arrival.
    else { gate.current.close(); setLink(null); setError(null); setCopied(false); setBusy(false); }
  }, [open, getLink]);

  // One-second tick, only while a live link is on screen.
  useEffect(() => {
    if (!open || !link) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open, link]);

  if (!open) return null;

  const left = link ? countdown(link.expiresAt, now) : null;
  const expired = left?.expired ?? false;

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      setError('Could not reach the clipboard on this device.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="w-full max-w-sm rounded-t-2xl bg-tg-bg p-4 shadow-xl sm:rounded-2xl lg:bg-panel">
        <header className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-title font-semibold">{title}</h2>
            <p className="truncate text-meta text-tg-hint">{subtitle}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-tg-hint lg:min-h-[40px] lg:min-w-[40px]">
            <X className="h-4 w-4" />
          </button>
        </header>

        {error && (
          // A refusal that means "already done" is not shown in the colour of a
          // failure: red on "Already signed" teaches people to ignore red.
          // words:ok — the refusal is the link's own sentence (lib/cosign-errors.ts linkRefusal)
          <StateLine
            tone={refusalIsGoodNews(error) ? 'good' : 'bad'}
            className="mb-3"
            action={!refusalIsGoodNews(error) ? <button type="button" className="btn-secondary min-h-[44px] text-body lg:min-h-[40px]" disabled={busy} onClick={() => void getLink()}>Try again</button> : undefined}
          >
            {linkRefusal(error)}
          </StateLine>
        )}

        {busy && <Pending what="Preparing a link…" />}

        {link && !expired && (
          <>
            <div className="mx-auto w-full max-w-[320px] rounded-xl bg-white p-3">
              <QrSvg text={link.url} className="h-auto w-full" />
            </div>

            <p className="mt-3 text-center text-body">Scan from TronLink's browser on the phone with your Ledger.</p>
            <p className={`mt-1 text-center font-mono text-meta tabular ${left?.urgent ? 'text-neg' : 'text-tg-hint'}`}>
              {left?.text}
            </p>

            <StateLine tone="warn" className="mt-3" more="This code is a live credential. Do not photograph it or send it to anyone — it expires on its own, and a new one is always one tap away.">
              A live credential — do not photograph or send it.
            </StateLine>

            <div className="mt-3 space-y-2">
              {/* Desktop with the TronLink extension injects tronWeb into an
                  ordinary tab, so a direct open genuinely works there. It does
                  not on mobile, which is why this is the secondary path. */}
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary flex min-h-[44px] w-full items-center justify-center gap-2 text-body"
              >
                Open here <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <p className="text-center text-meta text-tg-hint">Only with the TronLink extension.</p>

              <button onClick={copy} className="btn-secondary flex min-h-[44px] w-full items-center justify-center gap-2 text-body">
                <Copy className="h-3.5 w-3.5" /> {copied ? 'Copied' : 'Copy link'}
              </button>
              <p className="text-center text-meta text-tg-hint">Last resort — never paste it into a chat.</p>
            </div>
          </>
        )}

        {link && expired && (
          <div className="py-6 text-center">
            {/* Not "nothing was signed": the sheet cannot know, and it said so to people who had just signed. */}
            <StateLine className="justify-center" more="If you signed with it, your signature was recorded. Otherwise get a new link — they are short-lived on purpose.">
              This link has expired.
            </StateLine>
            <button onClick={() => void getLink()} className="btn-primary mt-4 inline-flex min-h-[44px] items-center gap-2 text-body">
              <RefreshCw className="h-3.5 w-3.5" /> Get a new link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
